import { DatePipe } from "@angular/common";
import { Component, OnInit, EventEmitter, Output, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateService, LangChangeEvent } from "@ngx-translate/core";
import { SessionService } from "@app/shared/services/session.service";
import { HeaderService } from '@app/shared/services/header.service';
import { LanguageResponse, UsersService } from '@src/app/shared/backend/clients';
import { firstValueFrom, Subject, takeUntil } from "rxjs";
import { UpdateLanguageRequest } from "@src/app/shared/backend/clients/model/updateLanguageRequest";
import { Popover } from "primeng/popover";

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    host: {
        class: 'app-header'
    },
    providers: [DatePipe],
    standalone: false
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Output() sidebarStatusEvent = new EventEmitter<boolean>();

  displaySidebar: boolean = true;

  canSwitchCompanies: boolean = false;
  customersListDropdown: any = [];
  languages: LanguageResponse[] = [];
  language: string | null = null;
  selectedLanguageValues: string[] = [];
  activeCompany: number[] = [];

  currentOptionText: string = '';
  currentCompanyId: number | null = null;

  userName: any;
  userCredentials: any;
  openNotificationsDropdown: boolean = false;
  openProfileDropdown: boolean = false;
  userRoles: any;
  isOtpTransparentLogin: boolean = false;

  private unsubscribe$ = new Subject<void>();
  private rolesLoaded: boolean = false;

  constructor(
    private translate: TranslateService,
    private usersService: UsersService,
    public session: SessionService,
    public router: Router,
    public headerService: HeaderService) {

    this.session.isSuperAdmin$().subscribe((isSuperAdmin) => {
      this.canSwitchCompanies = isSuperAdmin;
    });
  }

  ngOnInit() {
    this.init();
  }

  async init(): Promise<void> {
    this.currentOptionText = this.translate.instant('DROPDOWN.SELECTED_VALUE');

    this.translate.onLangChange
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((_event: LangChangeEvent) => {
        this.customersListDropdown = this.customersListDropdown.map((company: any) => ({
          ...company,
          text: this.removeCurrentValueSuffix(company?.text ?? '')
        }));

        this.currentOptionText = this.translate.instant('DROPDOWN.SELECTED_VALUE');
        this.markCurrentCompanyOption();
      });

    this.headerService.refreshCompanies$.pipe(
      takeUntil(this.unsubscribe$)).subscribe(() => {
        this.loadCompanies(true);
      });

    setTimeout(async () => {
      this.loadCompanies();
    }, 200);

    this.userName = (await (this.session.getUser()))?.username;

    if (this.userName) {
      const matches = this.userName?.split('@')[0]?.match(/\b(\w)/g);
      this.userCredentials = matches.join('')?.toUpperCase();
    }

    await this.getLanguages();
    setTimeout(() => {
      const currentLanguage = this.session.locale || localStorage.getItem('locale');
      this.language = currentLanguage ?? null;
      this.selectedLanguageValues = this.language ? [this.language] : [];
    }, 100);

    // Load user roles only once
    if (!this.rolesLoaded) {
      this.rolesLoaded = true;
      this.session.getUserRoleName().pipe(
        takeUntil(this.unsubscribe$)
      ).subscribe((roles) => {
        this.userRoles = roles;
      });
    }

    this.session.isOtpTransparentLogin$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isOtpLogin) => {
        this.isOtpTransparentLogin = isOtpLogin;
      });
  }

  onCustomersListDropdownChange(selectedValues: number[] | null): void {
    this.activeCompany = Array.isArray(selectedValues)
      ? selectedValues.map(x => Number(x))
      : [];

    const selectedCompanyId = this.activeCompany[0];
    if (selectedCompanyId == null) {
      return;
    }

    this.currentCompanyId = selectedCompanyId;
    this.session.setCompanyId(selectedCompanyId);

    this.markCurrentCompanyOption();

    // reset tables cache on company change
    this.session.clearTableStateKeys();
  }

  public toggleSidebar() {
    this.sidebarStatusEvent.emit();
  }

  public closePrimaryDropdown(popover: Popover): void {
    popover.hide();
  }

  public async loadCompanies(forceRefresh: boolean = false) {
    const companies = await this.session.getCompanies(forceRefresh);

    if (Array.isArray(companies)) {
      this.customersListDropdown = companies.sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
    } else {
      this.customersListDropdown = [];
      console.warn('No companies returned from session.getCompanies()');
    }

    const currentCompany = this.session.getCompany();
    const currentCompanyId =
      typeof currentCompany === 'object' ? currentCompany?.id : currentCompany;

    this.currentCompanyId = currentCompanyId != null ? Number(currentCompanyId) : null;
    this.activeCompany = this.currentCompanyId != null ? [this.currentCompanyId] : [];

    this.markCurrentCompanyOption();
  }

  private removeCurrentValueSuffix(text: string): string {
    if (!this.currentOptionText) {
      return text.trim();
    }

    return text.replace(this.currentOptionText, '').trim();
  }

  private markCurrentCompanyOption(): void {
    if (this.currentCompanyId == null) {
      return;
    }

    const listWithoutCurrentValue = (this.customersListDropdown ?? []).map((company: any) => ({
      ...company,
      text: this.removeCurrentValueSuffix(company?.text ?? '')
    }));

    const currentCompany = listWithoutCurrentValue.find(
      (company: any) => Number(company?.id) === Number(this.currentCompanyId)
    );

    if (!currentCompany) {
      return;
    }

    const currentCompanyWithLabel = {
      ...currentCompany,
      text: `${currentCompany.text} ${this.currentOptionText}`.trim()
    };

    this.customersListDropdown = [
      currentCompanyWithLabel,
      ...listWithoutCurrentValue.filter(
        (company: any) => Number(company?.id) !== Number(this.currentCompanyId)
      )
    ];
  }

  handleAppToggleClass(event: MouseEvent, className: string) {
    event.preventDefault();

    const elm = document.getElementById('app');
    if (elm) {
      elm.classList.toggle(className);
    }
  }

  async signOut() {
    await this.session.logout(true);
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  async selectLanguage(lang: string): Promise<void> {
    localStorage.setItem('locale', lang);
    this.session.locale = lang;
    this.language = lang;
    this.selectedLanguageValues = [lang];

    return new Promise((resolve) => {
      this.translate.use(lang).subscribe(() => {
        this.getLanguages();
        resolve();
      });
    });
  }

  async onLanguageChange(selectedValues: string[] | null): Promise<void> {
    this.selectedLanguageValues = Array.isArray(selectedValues) ? selectedValues : [];

    const selectedCode = this.selectedLanguageValues[0];
    if (!selectedCode) {
      return;
    }

    this.language = selectedCode;

    const selectedLanguage = this.languages.find(lang => lang.code === selectedCode);
    if (!selectedLanguage?.id) {
      return;
    }

    const payload: UpdateLanguageRequest = {
      languageId: selectedLanguage.id
    };

    await firstValueFrom(this.usersService.apiUsersLanguagePut(payload));
    await this.selectLanguage(selectedCode);
  }

  private getLanguages(): Promise<void> {
    const languages = this.session.companySettings?.languages ?? [];
    const enabledLanguages = languages.filter(language => language.isEnabled);

    return new Promise((resolve) => {
      this.translate.get('PORTAL_LANGUAGES').subscribe(translation => {
        const transformedLanguages = enabledLanguages
          .sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
          .map(lang => {
            const code = lang.code || 'unknown';
            const translationKey = `PORTAL_LANGUAGES.${code.toUpperCase()}`;
            return {
              id: lang.id,
              language: translationKey,
              code: code
            };
          });
        this.languages = transformedLanguages;
        resolve();
      });
    });
  }
}
